import React, { useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Camera, Package, Barcode } from 'lucide-react';

const ToolFormDialog = ({
  open,
  onOpenChange,
  formData,
  setFormData,
  editingId,
  onSubmit,
  onPhotoUpload,
  uploadingPhoto
}) => {
  const fileInputRef = useRef(null);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading">
            {editingId ? 'Modifier l\'outil' : 'Nouvel Outil'}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Photo</Label>
            <div className="flex items-center gap-4">
              {formData.photo ? (
                <img
                  src={formData.photo}
                  alt="Aperçu"
                  className="w-20 h-20 object-cover rounded-lg"
                />
              ) : (
                <div className="w-20 h-20 bg-muted rounded-lg flex items-center justify-center">
                  <Package className="w-8 h-8 text-muted-foreground" />
                </div>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={onPhotoUpload}
                className="hidden"
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingPhoto}
                className="rounded-full"
              >
                <Camera className="w-4 h-4 mr-2" />
                {uploadingPhoto ? 'Téléchargement...' : 'Ajouter une photo'}
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name">Nom *</Label>
              <Input
                id="name"
                data-testid="tool-name-input"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                className="rounded-lg"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="type">Type *</Label>
              <Input
                id="type"
                data-testid="tool-type-input"
                value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                required
                className="rounded-lg"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="article_number">Numéro d'article *</Label>
              <Input
                id="article_number"
                data-testid="tool-article-input"
                value={formData.article_number}
                onChange={(e) => setFormData({ ...formData, article_number: e.target.value })}
                required
                className="rounded-lg"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="barcode" className="flex items-center gap-1">
                <Barcode className="w-3 h-3" />
                Code-barres
              </Label>
              <Input
                id="barcode"
                data-testid="tool-barcode-input"
                value={formData.barcode || ''}
                onChange={(e) => setFormData({ ...formData, barcode: e.target.value })}
                placeholder="Scanner ou saisir le code"
                className="rounded-lg font-mono"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="order_number">Numéro de commande</Label>
              <Input
                id="order_number"
                data-testid="tool-order-input"
                value={formData.order_number}
                onChange={(e) => setFormData({ ...formData, order_number: e.target.value })}
                className="rounded-lg"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="supplier">Fournisseur</Label>
              <Input
                id="supplier"
                data-testid="tool-supplier-input"
                value={formData.supplier}
                onChange={(e) => setFormData({ ...formData, supplier: e.target.value })}
                className="rounded-lg"
              />
            </div>
            {!editingId && (
              <div className="space-y-2">
                <Label htmlFor="quantity">Quantité initiale *</Label>
                <Input
                  id="quantity"
                  data-testid="tool-quantity-input"
                  type="number"
                  min="1"
                  value={formData.quantity_total}
                  onChange={(e) => setFormData({ ...formData, quantity_total: parseInt(e.target.value) || 1 })}
                  required
                  className="rounded-lg"
                />
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="location">Localisation</Label>
            <Input
              id="location"
              data-testid="tool-location-input"
              value={formData.location}
              onChange={(e) => setFormData({ ...formData, location: e.target.value })}
              placeholder="Ex: Atelier A, Étagère 3..."
              className="rounded-lg"
            />
            <p className="text-xs text-muted-foreground">
              Optionnel - Emplacement de stockage de l'outil
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="expiration_date">Date d'expiration</Label>
            <Input
              id="expiration_date"
              data-testid="tool-expiration-input"
              type="date"
              value={formData.expiration_date}
              onChange={(e) => setFormData({ ...formData, expiration_date: e.target.value })}
              className="rounded-lg"
            />
            <p className="text-xs text-muted-foreground">
              Optionnel - Date de fin de garantie, certification, etc.
            </p>
          </div>

          <Button
            data-testid="tool-submit-button"
            type="submit"
            className="w-full rounded-full"
          >
            {editingId ? 'Mettre à jour' : 'Créer l\'outil'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ToolFormDialog;
